import { useQuery } from '@tanstack/react-query';
import { AskBar, Card, DashedCard, EmptyState, Label, ProjectCard, StatTile } from '@raro/ui';
import { Link, useNavigate } from 'react-router';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { PROJECT_STATUS_LABELS, PROJECT_TYPE_LABELS, formatNumber, greeting } from '../lib/format';
import type { Overview } from '../lib/types';

export function HojePage() {
  const { me } = useAuth();
  const navigate = useNavigate();
  const overview = useQuery({ queryKey: ['overview'], queryFn: () => api.get<Overview>('/overview') });
  const data = overview.data;

  return (
    <div className="rr-page rr-stack rr-stack--8">
      <header className="rr-stack rr-stack--5">
        <div className="rr-stack rr-stack--2">
          <p className="rr-heading rr-muted">{greeting()}</p>
          <h1 className="rr-display">o que precisamos enxergar hoje?</h1>
        </div>
        <AskBar
          hint="a Machine responde na MEMÓRIA, com citações. busca, pergunta, investiga."
          onSubmit={(q) => navigate(`/memoria?q=${encodeURIComponent(q)}`)}
        />
      </header>

      <section className="rr-section">
        <div className="rr-section__head">
          <Label>Projetos ativos</Label>
          <Link to="/projetos" className="rr-label rr-label--strong">
            todos
          </Link>
        </div>
        <div className="rr-grid-cards">
          {data?.activeProjects.map((p) => (
            <Link key={p.id} to={`/projetos/${p.id}`}>
              <Card interactive>
                <ProjectCard
                  organization={p.organization.name}
                  name={p.name}
                  cycleLabel={p.activeCycle ? `${p.activeCycle.name} · ${PROJECT_TYPE_LABELS[p.type]}` : PROJECT_TYPE_LABELS[p.type]}
                  statusLabel={PROJECT_STATUS_LABELS[p.status]}
                  statusTone="active"
                />
              </Card>
            </Link>
          ))}
          {me?.internalRole && (
            <Link to="/projetos/novo">
              <DashedCard>
                <span>
                  +<br />
                  novo projeto
                </span>
              </DashedCard>
            </Link>
          )}
        </div>
        {overview.isSuccess && data?.activeProjects.length === 0 && !me?.internalRole && (
          <EmptyState title="nenhum projeto ativo." />
        )}
      </section>

      <section className="rr-section">
        <Label>Atenção</Label>
        {data?.attention && (data.attention.signals.length + data.attention.contradictions.length + data.attention.openQuestions.length + (data.attention.connections?.length ?? 0) > 0) ? (
          <div className="rr-attention">
            {data.attention.signals.length > 0 && (
              <div className="rr-attention__col">
                <span className="rr-label rr-label--strong">novos sinais</span>
                {data.attention.signals.map((s) => (
                  <Link key={s.id} to={`/projetos/${s.projectId}`} className="rr-attention__item">
                    <span className="rr-attention__proj">{projectName(data, s.projectId)}</span>
                    <span><span className={s.direction > 0 ? 'rr-signal__icon rr-signal__icon--up' : s.direction < 0 ? 'rr-signal__icon rr-signal__icon--down' : 'rr-signal__icon rr-signal__icon--flat'}>{s.direction > 0 ? '↑ ' : s.direction < 0 ? '↓ ' : '↗ '}</span>{s.text}</span>
                  </Link>
                ))}
              </div>
            )}
            {data.attention.contradictions.length > 0 && (
              <div className="rr-attention__col">
                <span className="rr-label rr-label--strong">contradições</span>
                {data.attention.contradictions.map((r) => (
                  <Link key={r.id} to={r.toType === 'hypothesis' ? `/hipoteses/${r.toId}` : `/projetos/${projectOf(data, r)}`} className="rr-attention__item">
                    <span><span className="rr-faint">{r.from?.title.slice(0, 60)}</span> <span style={{ color: 'var(--rr-down)' }}>×</span> {r.to?.title}</span>
                  </Link>
                ))}
              </div>
            )}
            {(data.attention.connections?.length ?? 0) > 0 && (
              <div className="rr-attention__col">
                <span className="rr-label rr-label--strong">conexões inesperadas</span>
                {data.attention.connections!.map((c) => (
                  <Link key={c.id} to="/wall/controle" className="rr-attention__item">
                    <span className="rr-attention__proj">{c.aLabel} × {c.bLabel}</span>
                    <span>{c.pattern}</span>
                  </Link>
                ))}
              </div>
            )}
            {data.attention.openQuestions.length > 0 && (
              <div className="rr-attention__col">
                <span className="rr-label rr-label--strong">perguntas abertas</span>
                {data.attention.openQuestions.map((q) => (
                  <Link key={q.id} to={`/projetos/${q.projectId}`} className="rr-attention__item">
                    <span className="rr-attention__proj">{projectName(data, q.projectId)}</span>
                    <span>{q.text}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        ) : (
          <EmptyState title="nada pede atenção ainda.">sinais, contradições e perguntas abertas aparecem aqui.</EmptyState>
        )}
      </section>

      {data?.memory && (
        <section className="rr-section">
          <Label>Memória Raro</Label>
          <div className="rr-stat-row">
            <StatTile value={formatNumber(data.memory.organizations)} label="clientes" />
            <StatTile value={formatNumber(data.memory.projects)} label="projetos" />
            <StatTile value={formatNumber(data.memory.sources)} label="fontes" />
            <StatTile value={formatNumber(data.memory.evidence)} label="evidências" />
          </div>
        </section>
      )}
    </div>
  );
}

function projectName(o: Overview, projectId: string) {
  const p = [...o.activeProjects, ...o.otherProjects].find((x) => x.id === projectId);
  return p ? `${p.organization.name} · ${p.name}` : '';
}
function projectOf(o: Overview, r: { from: { id: string } | null }) {
  return o.activeProjects[0]?.id ?? '';
}
